class VariantRadiosWrapper extends HTMLElement {
  constructor() {
    super();

    // listen to the variant radios change on the Product summary and on the Purchase drawer
    this.querySelectorAll('[data-variant-radios]').forEach((variantRadio) =>
      variantRadio.addEventListener('change', this.onVariantChange.bind(this))
    );
    this.querySelectorAll('[data-option-quantity]').forEach((variantQuantity) =>
      variantQuantity.addEventListener('change', this.onBulkQuantityChange.bind(this))
    );

    // create the template with the needed elements for the user to add notes to the order on a variant level (only used when that is enabled in the block settings and the Bulk orders are enabled)
    this.variantNotesWrapper = this.querySelector('[data-variant-notes-wrapper]');
    if (this.variantNotesWrapper) {
      this.variantNoteTemplate = this.variantNotesWrapper.querySelector('[data-main-variant-note]');
      // add click event listener to the Previous Variant Note button - this will add/hide the check icon on the Add notes to order button
      const previousVariantNotesButton = this.querySelector('[data-variant-notes-previous-btn]');
      const addVariantNotesButtonIcon = this.querySelector('[data-add-variant-notes-button] .icon');
      previousVariantNotesButton.addEventListener('click', () => {
        const inputVariantNotes = this.variantNotesWrapper.querySelectorAll(
          '[data-variant-input-note]'
        );
        if (!addVariantNotesButtonIcon.classList.contains('hidden'))
          addVariantNotesButtonIcon.classList.add('hidden');
        // display check icon on Add notes to order button if at least 1 input has value
        for (const input of inputVariantNotes) {
          if (input.value) {
            addVariantNotesButtonIcon.classList.remove('hidden');
            break;
          }
        }
      });
    }

    // Run onBulkQuantityChange on page load
    window.addEventListener('load', () => {
      this.querySelectorAll('[data-option-quantity]').forEach((variantQuantity) => {
        this.onBulkQuantityChange({
          preventDefault: () => {},
          stopPropagation: () => {},
          stopImmediatePropagation: () => {},
        });
      });
    });

    // Add event listener to the Add to Cart button to reset quantity inputs
    const addButton = this.querySelector('[name="add"]');
    if (addButton) addButton.addEventListener('click', this.resetQuantityInputs.bind(this));
  }

  resetQuantityInputs() {
    setTimeout(() => {
      // Set all quantity selectors in the drawer to 0
      this.querySelectorAll('[data-option-quantity]').forEach((variantQuantity) => {
        variantQuantity.value = 0;
      });

      // Optionally, you can also call the onBulkQuantityChange after resetting if needed
      this.querySelectorAll('[data-option-quantity]').forEach((variantQuantity) => {
        this.onBulkQuantityChange({
          preventDefault: () => {},
          stopPropagation: () => {},
          stopImmediatePropagation: () => {},
        });
      });
    }, 1000);
  }

  onVariantChange() {
    // Immediately disable the add to cart button when variant changes, to prevent adding out of stock items to the cart
    this.toggleAddButton(true, window.variantStrings.loading);

    this.updateOptions();
    this.updateMasterId();

    if (!this.currentVariant) {
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.dispatchVariantChange();
    }
  }

  async onBulkQuantityChange(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    let disableButton = true;

    // Fetch the cart data
    const cart = await fetch('/cart.js')
      .then((response) => response.json())
      .catch((error) => {
        console.error('Error fetching cart data:', error);
        return { items: [] };
      });

    const cartItems = cart.items;

    this.querySelectorAll('[data-option-quantity]').forEach((variantQuantity) => {
      // enable ATC button when the qty is > 0 & show/hide the Stock information according to the qty
      const optionParent = variantQuantity.closest('[data-option]');
      const maxStockValue = +variantQuantity.dataset.variantStock;
      const maxStockText = optionParent.querySelector('[data-variant-maximum-stock]');
      const variantID = variantQuantity.dataset.bulkVariantId;
      const selectedVariantNote = this.variantNotesWrapper?.querySelector(
        `[data-variant-id-note="${variantID}"]`
      );

      if (variantQuantity.value > 0) {
        disableButton = false;
        if (!optionParent.hasAttribute('data-out-of-stock'))
          optionParent.classList.remove('stock-is-hidden');

        // set the Maximum amount reached text when the selected qty matches the variant inventory
        maxStockText?.classList.toggle('hidden', +variantQuantity.value >= maxStockValue);
        maxStockText?.classList.toggle('hidden', +variantQuantity.value < maxStockValue);

        // Update/Add Variant Note according to the Quantity
        if (this.variantNotesWrapper) {
          if (selectedVariantNote) {
            selectedVariantNote.querySelector('[data-variant-amount]').innerHTML =
              variantQuantity.value;
            selectedVariantNote.classList.remove('hidden');
          } else {
            this.createVariantNote(variantQuantity);
          }
        }
      } else {
        // if there was a Variant Note created for the deleted variant, delete it
        if (selectedVariantNote) selectedVariantNote.remove();

        // hide stock information
        if (!optionParent.hasAttribute('data-out-of-stock'))
          optionParent.classList.add('stock-is-hidden');
      }

      // display check icon on Add notes to order button if at least 1 input has value
      const addVariantNotesButtonIcon = this.querySelector('[data-add-variant-notes-button] .icon');
      const inputVariantNotes = this.variantNotesWrapper?.querySelectorAll(
        '[data-variant-input-note]'
      );
      if (addVariantNotesButtonIcon && inputVariantNotes) {
        if (!addVariantNotesButtonIcon.classList.contains('hidden'))
          addVariantNotesButtonIcon.classList.add('hidden');
        for (const input of inputVariantNotes) {
          if (input.value) {
            addVariantNotesButtonIcon.classList.remove('hidden');
            break;
          }
        }
      }

      // Check if the item is in the cart and check if backorder is enabled
      const cartItem = cartItems.find((item) => item.variant_id === Number(variantID));
      const backorderEnabled = document.querySelector('[data-backorder]').dataset.backorder;

      if (cartItem && backorderEnabled === 'false') {
        const plusButton = optionParent.querySelector('button[name="plus"]');
        const maxStockText = optionParent.querySelector('[data-variant-maximum-stock]');
        const maxStockCartText = optionParent.querySelector(
          '[data-variant-maximum-stock-with-cart]'
        );

        // Calculate the max available quantity based on the current cart quantity
        let maxAvailableQuantity = maxStockValue - cartItem.quantity;

        // Set the quantity input max to the max available quantity
        variantQuantity.max = maxAvailableQuantity;

        // Disable the plus button when the selected quantity matches the max available quantity
        if (variantQuantity.value >= maxAvailableQuantity) {
          if (plusButton) plusButton.disabled = true;

          maxStockCartText?.classList.remove('hidden');
          maxStockText?.classList.add('hidden');
          optionParent.classList.remove('stock-is-hidden');
        } else {
          if (plusButton) plusButton.disabled = false;
          maxStockCartText?.classList.add('hidden');
        }
      }
    });

    this.toggleAddButton(disableButton, '', false);
  }

  /**
   * createVariantNote
   * @param {Object} Bulk order quantity
   * @returns {void}
   * @description Creates and places an HTML element with an input to add a Variant note property to the order
   */
  createVariantNote(variantQuantity) {
    const currentVariantNote = this.variantNoteTemplate.cloneNode(true);
    // set position of the variant note according to the order + change data attr variant ID, quantity, option name and note label/input
    currentVariantNote.classList.add(`order-${variantQuantity.dataset.position}`);
    currentVariantNote.classList.remove('order-first', 'hidden');
    currentVariantNote.dataset.variantIdNote = variantQuantity.dataset.bulkVariantId;
    currentVariantNote.querySelector('[data-variant-amount]').innerHTML = variantQuantity.value;
    currentVariantNote.querySelector('[data-variant-size]').innerHTML =
      variantQuantity.dataset.bulkValue;
    const productNoteReference = `productNote${variantQuantity.dataset.position}`;
    currentVariantNote
      .querySelector('[data-variant-label-note]')
      .setAttribute('for', productNoteReference);
    currentVariantNote.querySelector('[data-variant-input-note]').id = productNoteReference;

    // insert Variant Note in the wrapper
    this.variantNotesWrapper.insertAdjacentElement('beforeend', currentVariantNote);
  }

  updateOptions() {
    // return if only the Default variant exists
    if (!this.querySelector('fieldset')) return (this.options = [undefined]);

    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      const radiosChecked = Array.from(fieldset.querySelectorAll('input')).find(
        (radio) => radio.checked
      );
      // before returning, check if there's a checked value - Size is not pre-selected on load
      if (radiosChecked) {
        return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
      } else {
        return undefined;
      }
    });

    // move the Size value to the right index on the options Array so it can match the Options order on Shopify Admin
    if (this.querySelector('[data-purchase-drawer]').querySelector('fieldset')) {
      const fromIndex = this.options.length - 1; // it will always be on the last position because it's loaded after the regular options
      const toIndex =
        this.querySelector('[data-purchase-drawer]').querySelector('fieldset').dataset
          .optionPosition;
      this.options.splice(toIndex, 0, this.options.splice(fromIndex, 1)[0]);
    }
  }

  updateMasterId() {
    // return if only the Default variant exists
    if (this.options.includes(undefined)) return;

    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  dispatchVariantChange() {
    document.dispatchEvent(
      new CustomEvent('variant-change', {
        bubbles: true,
        variant: this.currentVariant,
        detail: {
          variant: this.currentVariant,
        },
      })
    );
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant?.featured_media) return;
    const newMedia = document.querySelector(
      `[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`
    );
    if (!newMedia) return;
    const parent = newMedia.parentElement;
    parent.prepend(newMedia);
    window.setTimeout(() => {
      parent.scroll(0, 0);
    });
  }

  updateURL() {
    if (!this.currentVariant) return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#main-product-form, #product-form-installment`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  async renderProductInfo() {
    fetch(
      `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`
    )
      .then((response) => response.text())
      .then((responseText) => {
        const id = `price-${this.dataset.section}`;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(id);
        const source = html.getElementById(id);
        const productStockNotification = document.querySelector('product-stock-notification');
        const klaviyoApiFilled = document.querySelector('[data-klaviyo-api-filled]').dataset
          .klaviyoApiFilled;
        const klaviyoBisEnabled = document.querySelector('[data-klaviyo-bis-enabled]').dataset
          .klaviyoBisEnabled;

        if (source && destination) destination.innerHTML = source.innerHTML;

        document.getElementById(`price-${this.dataset.section}`)?.classList.remove('hidden');

        // Update the inventory quantity hidden input
        const inventoryQtyDestination = document
          .querySelector(`#main-product-form`)
          .querySelector('input[name="inventory_quantity"]');
        const inventoryQtySource = html
          .querySelector(`#main-product-form`)
          .querySelector('input[name="inventory_quantity"]');
        inventoryQtyDestination.value = inventoryQtySource.value;

        const continueSelling = html
          .querySelector(`#main-product-form`)
          .querySelector('input[name="continue_selling"]').value;

        let available = false;
        if (this.currentVariant) {
          available = this.currentVariant.available;
          if (available && window.productValues.tags.includes('alwaysbackorder')) {
            available = true;
          } else if (
            available &&
            inventoryQtyDestination.value <= 0 &&
            !window.userValues?.tags.includes('backorder')
          ) {
            available = false;
          }

          this.toggleAddButton(!available, window.variantStrings.soldOut);
        }

        // Update the stock information visibility - Only show when the variant is unavailable
        if (this.querySelector('[data-stock-information]')) {
          if (inventoryQtySource.value <= '0' && !available && continueSelling !== 'deny') {
            // update the Stock information element
            const stockElementDestination = this.querySelector('[data-stock-information]');
            const stockElementSource = html.querySelector('[data-stock-information]');
            stockElementDestination.innerHTML = stockElementSource.innerHTML;

            // hide the days to new stock when the value is <= 0
            const minutesToNewStock = +stockElementSource.querySelector(
              '[data-stock-variant-information]'
            ).dataset.stockVariantInformation;
            if (minutesToNewStock > 0) {
              this.querySelector('[data-stock-variant-information]').classList.remove('hidden');
            } else {
              this.querySelector('[data-stock-variant-information]').classList.add('hidden');
              this.querySelector('[data-stock-variant-information]').innerHTML = '';
            }

            if (klaviyoApiFilled == 'true' && klaviyoBisEnabled == 'true') {
              // show klaviyo bis form when the variant is out of stock
              this.querySelector('.purchase-drawer__footer').classList.add('hidden');
              this.querySelector('.purchase-drawer__stock-notification').classList.remove('hidden');

              // update the product variant ID
              this.querySelector('[data-variant-id]').dataset.variantId = this.currentVariant.id;

              productStockNotification.clearSubmit();
            } else {
              // show stock info
              this.querySelector('[data-stock-information]').classList.remove('hidden');
            }
          } else {
            if (klaviyoApiFilled == 'true' && klaviyoBisEnabled == 'true') {
              // hide klaviyo bis form when the variant is in stock
              this.querySelector('.purchase-drawer__footer').classList.remove('hidden');
              this.querySelector('.purchase-drawer__stock-notification').classList.add('hidden');
            }
            // hide stock info
            this.querySelector('[data-stock-information]').classList.add('hidden');
          }
        }

        // Update the Product collapsibles with Variant information on variant change
        let collapsibleWithVariantInfoDestination = this.closest(
          '[data-product-info]'
        ).querySelector('[data-product-collapsible-with-variant-info]');
        if (collapsibleWithVariantInfoDestination) {
          const collapsibleWithVariantInfoSource = html.querySelector(
            '[data-product-collapsible-with-variant-info]'
          );
          collapsibleWithVariantInfoDestination.innerHTML =
            collapsibleWithVariantInfoSource.innerHTML;
        }

        // Call checkCartAndInventory to validate the cart against the updated inventory
        document.querySelector('product-info').checkCartAndInventory();
      });
  }

  /**
   * toggleAddButton
   * @param {Boolean} disable
   * @param {String} text
   * @param {Boolean} modifyClass
   * @returns {void}
   */
  toggleAddButton(disable = true, text, modifyClass = true) {
    // get the button on the PDP Summary or the button on the Purchase drawer if there's one
    let addButton, initButton;

    const addVariantNotesButton = this.querySelector('[data-add-variant-notes-button]');
    const addPartnerDiscountButton = this.querySelector('[data-add-partner-discount-button]');

    const productForm = document.getElementById(`main-product-form`);
    if (this.querySelector('[data-purchase-drawer]')) {
      addButton = productForm
        ?.querySelector('[data-purchase-drawer]')
        .querySelector('[name="add"]');
      initButton = productForm
        ?.querySelector('[data-purchase-drawer]')
        .querySelector('[data-embroidery-init]');
    } else {
      addButton = productForm?.querySelector('[name="add"]');
      initButton = false;
    }

    const addButtonText = addButton?.querySelector('[data-add-to-cart-button-text]');
    if (!addButton || !addButtonText) return;

    if (disable) {
      addButton.setAttribute('disabled', true);
      initButton?.setAttribute('disabled', true);
      addVariantNotesButton?.setAttribute('disabled', true);
      addVariantNotesButton?.querySelector('.icon').classList.add('hidden');
      addPartnerDiscountButton?.setAttribute('disabled', true);
      addPartnerDiscountButton?.querySelector('.icon').classList.add('hidden');

      if (text) addButtonText.textContent = text;
    } else {
      // Check if the embroidery student configurator is present in the drawer, otherwise enable the button
      if (!productForm.querySelector('embroidery-configurator-student')) {
        addButton.removeAttribute('disabled');
        initButton?.removeAttribute('disabled');
        addVariantNotesButton?.removeAttribute('disabled');
        addPartnerDiscountButton?.removeAttribute('disabled');
        addButtonText.textContent = window.variantStrings.addToCart;
      }
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    // don't set the button to unavailable when the variants haven't been chosen
    if (this.options.includes(undefined)) {
      return;
    }

    let addButton;
    const productForm = document.getElementById(`main-product-form`);
    // get the button on the PDP Summary or the button on the Purchase drawer if there's one
    if (this.querySelector('[data-purchase-drawer]')) {
      addButton = productForm
        ?.querySelector('[data-purchase-drawer]')
        .querySelector('[name="add"]');
    } else {
      addButton = productForm?.querySelector('[name="add"]');
    }
    if (!addButton) return;
    addButton.textContent = window.variantStrings.unavailable;
    document.getElementById(`price-${this.dataset.section}`)?.classList.add('hidden');
  }

  getVariantData() {
    this.variantData =
      this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

if (!customElements.get('variants-and-buy-buttons')) {
  customElements.define('variants-and-buy-buttons', VariantRadiosWrapper);
}
